"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PCAD_POSITIVE_ASSERTIONS_ONLY_METADATA_KEY = exports.PCAD_REJECTED_CONNECTION_ROWS_METADATA_KEY = exports.PCAD_RAW_CONNECTIONS_METADATA_KEY = exports.PCAD_NO_CONNECT_PLACEHOLDER_NET_NAME = void 0;
exports.getPcadNetlistRawConnections = getPcadNetlistRawConnections;
exports.getPcadRejectedConnectionRows = getPcadRejectedConnectionRows;
exports.usesPcadPositiveAssertionsOnly = usesPcadPositiveAssertionsOnly;
exports.parsePcadNetlist = parsePcadNetlist;
exports.PCAD_NO_CONNECT_PLACEHOLDER_NET_NAME = '?';
exports.PCAD_RAW_CONNECTIONS_METADATA_KEY = 'pcad.rawConnections';
exports.PCAD_REJECTED_CONNECTION_ROWS_METADATA_KEY = 'pcad.rejectedConnectionRows';
exports.PCAD_POSITIVE_ASSERTIONS_ONLY_METADATA_KEY = 'pcad.positiveAssertionsOnly';
function isPcadRejectedConnectionRow(row) {
    if (typeof row !== 'object' || row === null) {
        return false;
    }
    const candidate = row;
    return typeof candidate.lineNumber === 'number';
}
function isPcadNetlistRawConnection(connection) {
    if (typeof connection !== 'object' || connection === null) {
        return false;
    }
    const candidate = connection;
    return (typeof candidate.moduleName === 'string' &&
        typeof candidate.pinRef === 'string' &&
        typeof candidate.netName === 'string');
}
function getPcadNetlistRawConnections(schematic) {
    const serialized = schematic.metadata?.[exports.PCAD_RAW_CONNECTIONS_METADATA_KEY];
    if (typeof serialized !== 'string') {
        return undefined;
    }
    try {
        const parsed = JSON.parse(serialized);
        if (!Array.isArray(parsed) || !parsed.every((connection) => isPcadNetlistRawConnection(connection))) {
            return undefined;
        }
        return parsed;
    }
    catch {
        return undefined;
    }
}
function getPcadRejectedConnectionRows(schematic) {
    const serialized = schematic.metadata?.[exports.PCAD_REJECTED_CONNECTION_ROWS_METADATA_KEY];
    if (typeof serialized !== 'string') {
        return undefined;
    }
    try {
        const parsed = JSON.parse(serialized);
        if (!Array.isArray(parsed) || !parsed.every((row) => isPcadRejectedConnectionRow(row))) {
            return undefined;
        }
        return parsed;
    }
    catch {
        return undefined;
    }
}
function usesPcadPositiveAssertionsOnly(schematic) {
    return schematic.metadata?.[exports.PCAD_POSITIVE_ASSERTIONS_ONLY_METADATA_KEY] === 'true';
}
function parsePcadNetlist(netlistContent, designId = 'default') {
    const warnings = [];
    const rejectedConnectionRows = [];
    const components = [];
    const parsedComponents = parsePcadComponents(netlistContent, warnings, rejectedConnectionRows);
    components.push(...parsedComponents);
    const modules = components.map((component) => convertToModule(component, warnings));
    const nets = buildNetsFromComponents(components);
    const rawConnections = components.flatMap((component) => component.pinConnections.map(({ pinRef, netName }) => ({
        moduleName: component.moduleName,
        pinRef,
        netName,
    })));
    const positiveAssertionsOnly = /^\s*\{COMPONENT\s+ORCAD\.PCB(?:\s|$)/i.test(netlistContent);
    const schematic = {
        modelType: 'schematic',
        designId,
        sourceFile: 'pcad-netlist',
        sheetIndex: 0,
        layerSet: 'default',
        drawings: {},
        edges_bbox: { minx: 0, miny: 0, maxx: 0, maxy: 0 },
        initial_zoombox: { minx: 0, miny: 0, maxx: 0, maxy: 0 },
        nets,
        modules,
        ports: {},
        metadata: {
            title: 'PCAD Netlist Import',
            revision: '',
            company: '',
            date: '',
            [exports.PCAD_RAW_CONNECTIONS_METADATA_KEY]: JSON.stringify(rawConnections),
            [exports.PCAD_REJECTED_CONNECTION_ROWS_METADATA_KEY]: JSON.stringify(rejectedConnectionRows),
            ...(positiveAssertionsOnly ? { [exports.PCAD_POSITIVE_ASSERTIONS_ONLY_METADATA_KEY]: 'true' } : {}),
        },
        warnings,
    };
    return { schematic, rawConnections, warnings, rejectedConnectionRows };
}
const PCAD_PIN_FIELD = /^[^,\s]+(?:\s*,\s*[^,\s]+)*$/;
function readPcadRowAlternatives(rawLine) {
    const line = rawLine.trimEnd();
    const readings = [];
    for (let column = 0; column < line.length; column++) {
        if (/\s/.test(line[column]) || (column > 0 && !/\s/.test(line[column - 1]))) {
            continue;
        }
        const pinField = line.slice(0, column).trim();
        if (pinField.length > 0 && !PCAD_PIN_FIELD.test(pinField)) {
            continue;
        }
        readings.push({
            column,
            pinRef: pinField.replace(/\s*,\s*/g, ', '),
            netName: line.slice(column),
            overflows: pinField.length > 0 && !/\s/.test(line[0]),
        });
    }
    return readings;
}
function readingFitsColumn(reading, column) {
    return reading.column === column || (reading.overflows && reading.column > column);
}
function inferPcadNetNameColumns(blocks, warnings) {
    const rows = blocks.flatMap((block) => block.cnRows.map(({ readings }) => readings));
    const candidates = new Set(rows.flatMap((readings) => readings.map(({ column }) => column)));
    const columns = [...candidates].filter((column) => rows.every((readings) => readings.some((reading) => readingFitsColumn(reading, column))));
    if (columns.length === 1 || rows.length === 0) {
        return columns;
    }
    if (columns.length > 1) {
        const expressibleColumns = columns.filter((column) => blocks.every((block) => block.cnRows.filter(({ readings }) => {
            const matchingReadings = readings.filter((reading) => readingFitsColumn(reading, column));
            return matchingReadings.length === 1 && matchingReadings[0].pinRef === '';
        }).length < 2));
        if (expressibleColumns.length === 1) {
            return expressibleColumns;
        }
    }
    warnings.push(columns.length === 0 ? 'pcad-layout-conflict' : 'pcad-layout-unanchored');
    return [];
}
function scanPcadComponentBlocks(lines) {
    const blocks = [];
    let inSubcomp = false;
    let inComponent = false;
    let inCnSection = false;
    let currentBlock = null;
    let braceDepth = 0;
    for (let i = 0; i < lines.length; i++) {
        const rawLine = lines[i];
        const line = rawLine.trim();
        if (!line || line.startsWith('//') || line.startsWith('#')) {
            continue;
        }
        if (line.includes('{SUBCOMP')) {
            inSubcomp = true;
            continue;
        }
        if (!inSubcomp) {
            continue;
        }
        const componentMatch = line.match(/^\{I\s+(.*?)\.PRT\s*(.*)$/);
        if (componentMatch) {
            if (currentBlock && currentBlock.moduleName) {
                blocks.push(currentBlock);
            }
            currentBlock = {
                footprintName: componentMatch[1].trim(),
                moduleName: componentMatch[2].trim(),
                cnRows: [],
            };
            inComponent = true;
            braceDepth = 1;
            continue;
        }
        if (inComponent) {
            if (line === '{CN') {
                inCnSection = true;
                braceDepth++;
                continue;
            }
            if (line === '}') {
                braceDepth--;
                if (inCnSection && braceDepth === 1) {
                    inCnSection = false;
                }
                else if (braceDepth === 0) {
                    if (currentBlock && currentBlock.moduleName) {
                        blocks.push(currentBlock);
                    }
                    currentBlock = null;
                    inComponent = false;
                    inCnSection = false;
                }
                continue;
            }
            if (inCnSection && currentBlock) {
                currentBlock.cnRows.push({ lineNumber: i + 1, rawLine, readings: readPcadRowAlternatives(rawLine) });
            }
        }
        if (line === '}' && inSubcomp && !inComponent) {
            inSubcomp = false;
            continue;
        }
    }
    if (currentBlock && currentBlock.moduleName) {
        blocks.push(currentBlock);
    }
    return blocks;
}
function interpretPcadComponentBlock(block, netNameColumns, warnings, rejectedConnectionRows) {
    const component = {
        footprintName: block.footprintName,
        moduleName: block.moduleName,
        pinNets: {},
        pinConnections: [],
    };
    for (const { lineNumber, rawLine, readings: rowReadings } of block.cnRows) {
        const readings = rowReadings.filter((reading) => netNameColumns.some((column) => readingFitsColumn(reading, column)));
        const resolvedReading = readings.length === 1 ? readings[0] : undefined;
        if (resolvedReading === undefined) {
            warnings.push(`pcad-row-ambiguous at line ${lineNumber}: ${rawLine.trim()}`);
            rejectedConnectionRows.push({ lineNumber });
            continue;
        }
        if (resolvedReading.pinRef === '') {
            if (component.pinNets[''] !== undefined) {
                warnings.push(`Component ${block.moduleName} declares more than one blank-pin connection row; rejecting the row at line ${lineNumber}`);
                rejectedConnectionRows.push({ lineNumber });
                continue;
            }
            component.pinConnections.push({ pinRef: '', netName: resolvedReading.netName });
            component.pinNets[''] = resolvedReading.netName;
            continue;
        }
        {
            const pinPart = resolvedReading.pinRef;
            const netName = resolvedReading.netName;
            if (component.pinNets[pinPart] !== undefined) {
                warnings.push(`Component ${block.moduleName} pin ${pinPart} appears in multiple nets; retaining ${netName} in pinNets`);
            }
            component.pinConnections.push({ pinRef: pinPart, netName });
            component.pinNets[pinPart] = netName;
            continue;
        }
    }
    return component;
}
function parsePcadComponents(content, warnings, rejectedConnectionRows) {
    const blocks = scanPcadComponentBlocks(content.split('\n'));
    const columns = inferPcadNetNameColumns(blocks, warnings);
    return blocks.map((block) => interpretPcadComponentBlock(block, columns, warnings, rejectedConnectionRows));
}
function convertToModule(component, _warnings) {
    const transform = {
        angle: 0,
        mirrored: [false, false],
    };
    return {
        ref: component.moduleName,
        footprintRef: component.footprintName,
        componentRef: component.footprintName,
        center: [0, 0],
        layer: 'F',
        transform,
        name: component.moduleName,
        pinNets: component.pinNets,
        drawProperties: {},
    };
}
function buildNetsFromComponents(components) {
    const nets = {};
    for (const component of components) {
        for (const [pinRef, netName] of Object.entries(component.pinNets)) {
            if (!nets[netName]) {
                nets[netName] = {
                    name: netName,
                    connections: [],
                };
            }
            const connection = {
                moduleRef: component.moduleName,
                moduleName: component.moduleName,
                pinRef,
            };
            nets[netName].connections.push(connection);
        }
    }
    return nets;
}
//# sourceMappingURL=pcadNetlistParser.js.map